import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { getCurrentUser, fetchAuthSession, signOut } from 'aws-amplify/auth';
import Navbar from './components/Navbar';
import Home from './components/Home';
import DicomWeb from './components/DicomWeb';
import StowUploader from './components/StowUploader';
import StudyDetails from './components/StudyDetails';
import NotFound from './components/NotFound';
import DicomViewer from './components/DicomViewer';
import DicomSeriesViewer from './components/DicomSeriesViewer';
import Login from './components/Login';

export const AuthContext = React.createContext({
  token: null,
  userInfo: null,
  apiBaseUrl: process.env.REACT_APP_API_URL || ''
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [token, setToken] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  const applySession = useCallback(async () => {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();
    setToken(session.tokens?.accessToken?.toString() || null);
    setUserInfo({
      preferred_username: user.username,
      email: user.signInDetails?.loginId || user.username,
    });
    setIsAuthenticated(true);
  }, []);

  useEffect(() => {
    applySession().catch(() => setIsAuthenticated(false));
  }, [applySession]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const id = setInterval(async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        setToken(session.tokens?.accessToken?.toString() || null);
      } catch {
        setIsAuthenticated(false);
      }
    }, 55 * 60 * 1000);
    return () => clearInterval(id);
  }, [isAuthenticated]);

  async function handleSignIn() {
    await applySession().catch(() => setIsAuthenticated(false));
  }

  async function handleSignOut() {
    await signOut();
    setIsAuthenticated(false);
    setToken(null);
    setUserInfo(null);
  }

  if (isAuthenticated === null) {
    return (
      <div className="App">
        <div className="loading">
          <div className="spinner"></div>
          <p>Initializing DICOMVii...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onSignIn={handleSignIn} />;
  }

  const authContextValue = {
    token,
    userInfo,
    apiBaseUrl: process.env.REACT_APP_API_URL || '',
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      <Router>
        <div className="App">
          <Navbar userInfo={userInfo} onSignOut={handleSignOut} />
          <div className="content">
            <Switch>
              <Route exact path="/">
                <Home />
              </Route>

              <Route path="/dicom-web/upload">
                <StowUploader />
              </Route>

              <Route path="/dicom-web">
                <DicomWeb />
              </Route>

              <Route path="/dicom">
                <DicomViewer />
              </Route>

              <Route path="/viewer/series/:seriesUid">
                <DicomSeriesViewer />
              </Route>

              <Route path="/studies/:id">
                <StudyDetails />
              </Route>

              <Route path="*">
                <NotFound />
              </Route>
            </Switch>
          </div>
        </div>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;
